// src/pages/inspector/InspectorInspections.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import InspectorLayout from '../../components/inspector/InspectorLayout';
import inspectorAPI from '../../services/inspectorAPI';
import { StatusBadge } from './InspectorDashboard';
import styles from './InspectorInspections.module.css';

const TIME_LABELS = { morning: '9AM–12PM', afternoon: '12PM–4PM', evening: '4PM–7PM' };

const FILTERS = ['all', 'pending', 'scheduled', 'in_progress', 'completed', 'approved', 'rejected'];

export default function InspectorInspections() {
  const [inspections, setInspections] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await inspectorAPI.getInspections();
        setInspections(res.data);
        setFiltered(res.data);
      } catch {
        setError('Failed to load inspections.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    let list = inspections;
    if (activeFilter !== 'all') {
      list = list.filter((i) => i.status === activeFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (i) =>
          i.booking_reference?.toLowerCase().includes(q) ||
          i.car?.brand?.toLowerCase().includes(q) ||
          i.car?.model?.toLowerCase().includes(q) ||
          i.city?.toLowerCase().includes(q)
      );
    }
    setFiltered(list);
  }, [activeFilter, search, inspections]);

  return (
    <InspectorLayout>
      <div className={styles.page}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>My Inspections</h1>
            <p className={styles.subtitle}>{filtered.length} inspection{filtered.length !== 1 ? 's' : ''} found</p>
          </div>
        </div>

        {/* Search + Filter */}
        <div className={styles.toolbar}>
          <input
            className={styles.search}
            type="text"
            placeholder="Search by car, city, reference…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className={styles.filterTabs}>
            {FILTERS.map((f) => (
              <button
                key={f}
                className={`${styles.filterTab} ${activeFilter === f ? styles.active : ''}`}
                onClick={() => setActiveFilter(f)}
              >
                {f.replace('_', ' ')}
                <span className={styles.filterCount}>
                  {f === 'all' ? inspections.length : inspections.filter((i) => i.status === f).length}
                </span>
              </button>
            ))}
          </div>
        </div>

        {error && <div className={styles.errorBanner}>{error}</div>}

        {loading ? (
          <div className={styles.loading}>Loading inspections…</div>
        ) : filtered.length === 0 ? (
          <div className={styles.empty}>
            <span>📭</span>
            <p>No inspections match your filters</p>
          </div>
        ) : (
          <div className={styles.grid}>
            {filtered.map((ins) => (
              <InspectionCard key={ins.id} ins={ins} />
            ))}
          </div>
        )}
      </div>
    </InspectorLayout>
  );
}

function InspectionCard({ ins }) {
  return (
    <Link to={`/inspector/inspections/${ins.id}`} className={styles.card}>
      <div className={styles.cardTop}>
        <div>
          <div className={styles.carName}>
            {ins.car.brand} {ins.car.model}
          </div>
          <div className={styles.carYear}>{ins.car.year} · {ins.car.km_driven?.toLocaleString()} km</div>
        </div>
        <StatusBadge status={ins.status} />
      </div>

      <div className={styles.cardDivider} />

      <div className={styles.cardMeta}>
        <MetaItem icon="🏷️" label={ins.booking_reference} />
        <MetaItem icon="📍" label={ins.city} />
        <MetaItem
          icon="📅"
          label={new Date(ins.preferred_date).toLocaleDateString('en-IN', {
            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
          })}
        />
        <MetaItem icon="⏰" label={TIME_LABELS[ins.preferred_time] || ins.preferred_time} />
      </div>

      <div className={styles.cardFooter}>
        <span className={styles.viewLink}>View Details →</span>
      </div>
    </Link>
  );
}

function MetaItem({ icon, label }) {
  return (
    <div className={styles.metaItem}>
      <span>{icon}</span>
      <span>{label}</span>
    </div>
  );
}